import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Building2,
  Plus,
  Pencil,
  Trash2,
  MoreVertical,
  Users,
  FileText,
  Image,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  ShieldAlert,
} from 'lucide-react'
import { useAuth } from '@/lib/contexts/auth-context'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { DataTable, type ColumnDef, useDataTable } from '@/components/ui/data-table'
import { useToast } from '@/components/ui/use-toast'
import { PageHeader, FluentCard } from '@/components/layout/page-header'
import { tenantsService, type Tenant, type CreateTenantRequest } from '@/lib/api/services/tenants'

interface DeleteDialogState {
  open: boolean
  tenant: Tenant | null
  forceDelete: boolean
}

interface EditDialogState {
  open: boolean
  tenant: Tenant | null
}

export default function CompaniesPage() {
  const navigate = useNavigate()
  const { hasPermission, isLoading: authLoading } = useAuth()
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check if user has system.admin permission (super admin only)
  const canAccessPage = hasPermission('system.admin')

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to access the Companies management page.
          This section is restricted to system administrators only.
        </p>
        <Button onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      </div>
    )
  }
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    tenant: null,
    forceDelete: false,
  })
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialog, setEditDialog] = useState<EditDialogState>({ open: false, tenant: null })
  const [formLoading, setFormLoading] = useState(false)
  const [subdomainStatus, setSubdomainStatus] = useState<{
    checking: boolean
    available: boolean | null
    message: string
  }>({ checking: false, available: null, message: '' })

  const { toast } = useToast()

  const {
    page,
    pageSize,
    sortField,
    sortDirection,
    search,
    filters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    setFilters,
  } = useDataTable({
    initialState: {
      pageSize: 20,
      sortField: 'name',
      sortDirection: 'asc',
    },
  })

  const loadTenants = useCallback(async () => {
    // Double-check permission before making API call
    if (!hasPermission('system.admin')) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const response = await tenantsService.getTenants({
        pageNumber: page,
        pageSize,
        searchTerm: search || undefined,
        sortBy: sortField,
        sortDescending: sortDirection === 'desc',
      })
      setTenants(response.items || [])
      setTotalCount(response.totalCount || 0)
    } catch (_error) {
      setError('Failed to load companies. You may not have permission to view this page.')
      toast({
        title: 'Error',
        description: 'Failed to load companies',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, search, sortField, sortDirection, toast, hasPermission])

  useEffect(() => {
    // Only load tenants if auth is loaded AND user has permission
    if (!authLoading && canAccessPage) {
      loadTenants()
    }
  }, [loadTenants, canAccessPage, authLoading])

  const checkSubdomain = useCallback(async (subdomain: string) => {
    if (!subdomain || subdomain.length < 3) {
      setSubdomainStatus({ checking: false, available: null, message: '' })
      return
    }

    setSubdomainStatus({ checking: true, available: null, message: '' })
    try {
      const result = await tenantsService.checkSubdomain(subdomain)
      setSubdomainStatus({
        checking: false,
        available: result.isAvailable,
        message: result.message,
      })
    } catch {
      setSubdomainStatus({
        checking: false,
        available: null,
        message: 'Failed to check subdomain',
      })
    }
  }, [])

  const handleCreateTenant = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)

    const data: CreateTenantRequest = {
      name: formData.get('name') as string,
      subdomain: (formData.get('subdomain') as string).toLowerCase(),
      planType: formData.get('planType') as string,
      adminEmail: formData.get('adminEmail') as string || undefined,
      adminFirstName: formData.get('adminFirstName') as string || undefined,
      adminLastName: formData.get('adminLastName') as string || undefined,
      adminPassword: formData.get('adminPassword') as string || undefined,
    }

    setFormLoading(true)
    try {
      await tenantsService.createTenant(data)
      toast({
        title: 'Success',
        description: `Company "${data.name}" created successfully`,
      })
      setCreateDialogOpen(false)
      loadTenants()
    } catch (err) {
      toast({
        title: 'Error',
        description: (err as Error).message || 'Failed to create company',
        variant: 'destructive',
      })
    } finally {
      setFormLoading(false)
    }
  }

  const handleUpdateTenant = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!editDialog.tenant) return

    const formData = new FormData(e.currentTarget)

    setFormLoading(true)
    try {
      await tenantsService.updateTenant(editDialog.tenant.id, {
        id: editDialog.tenant.id,
        name: formData.get('name') as string,
        planType: formData.get('planType') as string,
        isActive: formData.get('isActive') === 'true',
      })
      toast({
        title: 'Success',
        description: 'Company updated successfully',
      })
      setEditDialog({ open: false, tenant: null })
      loadTenants()
    } catch (err) {
      toast({
        title: 'Error',
        description: (err as Error).message || 'Failed to update company',
        variant: 'destructive',
      })
    } finally {
      setFormLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteDialog.tenant) return

    setDeleteLoading(true)
    try {
      await tenantsService.deleteTenant(deleteDialog.tenant.id, deleteDialog.forceDelete)
      toast({
        title: 'Success',
        description: deleteDialog.forceDelete
          ? 'Company permanently deleted'
          : 'Company deactivated',
      })
      setDeleteDialog({ open: false, tenant: null, forceDelete: false })
      loadTenants()
    } catch (err) {
      toast({
        title: 'Error',
        description: (err as Error).message || 'Failed to delete company',
        variant: 'destructive',
      })
    } finally {
      setDeleteLoading(false)
    }
  }

  const planColors: Record<string, string> = {
    Free: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    Basic: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    Professional: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    Enterprise: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  }

  const columns: ColumnDef<Tenant>[] = [
    {
      id: 'name',
      header: 'Company',
      accessor: 'name',
      sortable: true,
      cell: (tenant) => (
        <div
          className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          onClick={() => navigate(`/admin/companies/${tenant.id}`)}
        >
          <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-400 to-indigo-600">
            <Building2 className="h-4 w-4 text-white" />
          </div>
          <div>
            <span className="font-medium text-gray-900 dark:text-white block hover:text-indigo-600 dark:hover:text-indigo-400">
              {tenant.name}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {tenant.subdomain}.templatean.com
            </span>
          </div>
        </div>
      ),
    },
    {
      id: 'planType',
      header: 'Plan',
      accessor: 'planType',
      sortable: true,
      width: '120px',
      cell: (tenant) => (
        <Badge className={planColors[tenant.planType] || planColors.Free}>
          {tenant.planType}
        </Badge>
      ),
    },
    {
      id: 'isActive',
      header: 'Status',
      accessor: 'isActive',
      sortable: true,
      width: '100px',
      cell: (tenant) => (
        <div className="flex items-center gap-1.5">
          {tenant.isActive ? (
            <>
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-600 dark:text-green-400">Active</span>
            </>
          ) : (
            <>
              <XCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-600 dark:text-red-400">Inactive</span>
            </>
          )}
        </div>
      ),
    },
    {
      id: 'userCount',
      header: 'Users',
      accessor: 'userCount',
      sortable: true,
      width: '80px',
      cell: (tenant) => (
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
          <Users className="h-4 w-4" />
          <span>{tenant.userCount}</span>
        </div>
      ),
    },
    {
      id: 'templateCount',
      header: 'Templates',
      accessor: 'templateCount',
      sortable: true,
      width: '100px',
      cell: (tenant) => (
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
          <FileText className="h-4 w-4" />
          <span>{tenant.templateCount}</span>
        </div>
      ),
    },
    {
      id: 'assetCount',
      header: 'Assets',
      accessor: 'assetCount',
      sortable: true,
      width: '80px',
      cell: (tenant) => (
        <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
          <Image className="h-4 w-4" />
          <span>{tenant.assetCount}</span>
        </div>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessor: 'createdAt',
      sortable: true,
      width: '120px',
      cell: (tenant) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {new Date(tenant.createdAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      accessor: 'id',
      width: '60px',
      cell: (tenant) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <MoreVertical className="h-5 w-5 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-xl">
            <DropdownMenuItem
              onClick={() => navigate(`/admin/companies/${tenant.id}`)}
              className="rounded-lg gap-2"
            >
              <Eye className="h-4 w-4" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setEditDialog({ open: true, tenant })}
              className="rounded-lg gap-2"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-amber-600 dark:text-amber-400 rounded-lg gap-2"
              onClick={() => setDeleteDialog({ open: true, tenant, forceDelete: false })}
            >
              <XCircle className="h-4 w-4" />
              Deactivate
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600 dark:text-red-400 rounded-lg gap-2"
              onClick={() => setDeleteDialog({ open: true, tenant, forceDelete: true })}
            >
              <Trash2 className="h-4 w-4" />
              Delete Permanently
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ]

  // Calculate stats
  const stats = {
    total: totalCount,
    active: tenants.filter((t) => t.isActive).length,
    totalUsers: tenants.reduce((sum, t) => sum + t.userCount, 0),
    totalTemplates: tenants.reduce((sum, t) => sum + t.templateCount, 0),
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Companies"
        description="Manage companies and their subscriptions"
        actions={
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="gap-2 rounded-full"
          >
            <Plus className="h-4 w-4" />
            Add Company
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        {[
          { label: 'Total Companies', value: stats.total, icon: Building2, color: 'from-indigo-400 to-indigo-600' },
          { label: 'Active', value: stats.active, icon: CheckCircle, color: 'from-green-400 to-green-600' },
          { label: 'Total Users', value: stats.totalUsers, icon: Users, color: 'from-blue-400 to-blue-600' },
          { label: 'Total Templates', value: stats.totalTemplates, icon: FileText, color: 'from-purple-400 to-purple-600' },
        ].map((stat) => (
          <FluentCard key={stat.label} className="flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color}`}>
              <stat.icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
            </div>
          </FluentCard>
        ))}
      </div>

      {/* Companies Table */}
      <FluentCard className="overflow-hidden">
        <DataTable
          columns={columns}
          data={tenants}
          totalCount={totalCount}
          loading={loading}
          error={error}
          onRetry={loadTenants}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sortField={sortField}
          sortDirection={sortDirection}
          onSortChange={setSort}
          filters={filters}
          onFilterChange={setFilters}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search companies..."
          rowKey="id"
          emptyMessage="No companies found"
        />
      </FluentCard>

      {/* Create Company Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Company</DialogTitle>
            <DialogDescription>
              Add a new company to the platform. An admin user will be created if credentials are provided.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTenant}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Company Name</Label>
                <Input id="name" name="name" placeholder="ACME Corporation" required />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subdomain">Subdomain</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="subdomain"
                    name="subdomain"
                    placeholder="acme"
                    required
                    pattern="^[a-z][a-z0-9-]*[a-z0-9]$"
                    minLength={3}
                    maxLength={63}
                    onChange={(e) => checkSubdomain(e.target.value)}
                  />
                  <span className="text-sm text-gray-500">.templatean.com</span>
                </div>
                {subdomainStatus.checking && (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Checking...
                  </p>
                )}
                {subdomainStatus.available === true && (
                  <p className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Available
                  </p>
                )}
                {subdomainStatus.available === false && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <XCircle className="h-3 w-3" /> {subdomainStatus.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="planType">Plan</Label>
                <Select name="planType" defaultValue="Free">
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Free">Free</SelectItem>
                    <SelectItem value="Basic">Basic</SelectItem>
                    <SelectItem value="Professional">Professional</SelectItem>
                    <SelectItem value="Enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-4 mt-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Initial Admin User (Optional)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="adminFirstName">First Name</Label>
                    <Input id="adminFirstName" name="adminFirstName" placeholder="John" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="adminLastName">Last Name</Label>
                    <Input id="adminLastName" name="adminLastName" placeholder="Doe" />
                  </div>
                </div>
                <div className="space-y-2 mt-3">
                  <Label htmlFor="adminEmail">Email</Label>
                  <Input id="adminEmail" name="adminEmail" type="email" placeholder="admin@company.com" />
                </div>
                <div className="space-y-2 mt-3">
                  <Label htmlFor="adminPassword">Password</Label>
                  <Input id="adminPassword" name="adminPassword" type="password" placeholder="••••••••" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading || subdomainStatus.available === false}>
                {formLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Company'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, tenant: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Company</DialogTitle>
            <DialogDescription>
              Update company details and settings.
            </DialogDescription>
          </DialogHeader>
          {editDialog.tenant && (
            <form onSubmit={handleUpdateTenant}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Company Name</Label>
                  <Input
                    id="edit-name"
                    name="name"
                    defaultValue={editDialog.tenant.name}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Subdomain</Label>
                  <Input
                    value={editDialog.tenant.subdomain}
                    disabled
                    className="bg-gray-50 dark:bg-gray-800"
                  />
                  <p className="text-xs text-gray-500">Subdomain cannot be changed</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-planType">Plan</Label>
                  <Select name="planType" defaultValue={editDialog.tenant.planType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Free">Free</SelectItem>
                      <SelectItem value="Basic">Basic</SelectItem>
                      <SelectItem value="Professional">Professional</SelectItem>
                      <SelectItem value="Enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-isActive">Status</Label>
                  <Select name="isActive" defaultValue={editDialog.tenant.isActive ? 'true' : 'false'}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditDialog({ open: false, tenant: null })}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading}>
                  {formLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, tenant: null, forceDelete: false })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className={deleteDialog.forceDelete ? 'h-5 w-5 text-red-600' : 'h-5 w-5 text-amber-600'} />
              {deleteDialog.forceDelete ? 'Permanently Delete Company' : 'Deactivate Company'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.forceDelete ? (
                <>
                  Are you sure you want to permanently delete "{deleteDialog.tenant?.name}"?
                  <strong className="block mt-2 text-red-600">
                    This will delete all users, templates, assets, and data associated with this company.
                    This action cannot be undone.
                  </strong>
                </>
              ) : (
                <>
                  Are you sure you want to deactivate "{deleteDialog.tenant?.name}"?
                  Users will no longer be able to log in, but all data will be preserved.
                  You can reactivate the company later.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={deleteDialog.forceDelete ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading
                ? 'Processing...'
                : deleteDialog.forceDelete
                  ? 'Delete Permanently'
                  : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
